import { useCallback, useState, type DragEvent, type RefObject } from 'react';
import { newDocId } from '../doc/clipIds';
import { insertClip } from '../doc/clipOps';
import { useClipLimits } from '../doc/useClipLimits';
import { isFootageDrag, readFootageDragData } from '../footage/footageDrag';
import { useMusicVideoEditor } from '../MusicVideoEditorContext';
import type { DragGuide } from './TimelineGuide';
import { dropLengthSec, pointerToSec, snapTime, type TimelineView } from './timelineGeometry';

interface UseLaneDropProps {
  laneRef: RefObject<HTMLDivElement | null>;
  view: TimelineView;
  snapOn: boolean;
  onGuide: (guide: DragGuide | null) => void;
}

/**
 * A footage card dropped on the footage lane (design c5): the clip goes in at the beat under the
 * pointer (Alt: exactly there) and covers `DROP_CLIP_BEATS` beats. The new clip is selected.
 */
export function useLaneDrop({ laneRef, view, snapOn, onGuide }: UseLaneDropProps) {
  const { change, songMap, select, readOnly } = useMusicVideoEditor();
  const limitsFor = useClipLimits();
  const [isOver, setIsOver] = useState(false);

  const placeOf = useCallback(
    (event: DragEvent<HTMLElement>): DragGuide | null => {
      const lane = laneRef.current;
      if (lane === null) {
        return null;
      }
      const free = !snapOn || event.altKey;
      const sec = snapTime(
        pointerToSec(event.clientX, lane.getBoundingClientRect(), view),
        songMap.beats,
        songMap.bpm,
        free
      );
      return { sec, free };
    },
    [laneRef, snapOn, songMap.beats, songMap.bpm, view]
  );

  const onDragOver = useCallback(
    (event: DragEvent<HTMLElement>) => {
      if (readOnly || !isFootageDrag(event.dataTransfer)) {
        return;
      }
      event.preventDefault();
      event.dataTransfer.dropEffect = 'copy';
      setIsOver(true);
      onGuide(placeOf(event));
    },
    [onGuide, placeOf, readOnly]
  );

  const onDragLeave = useCallback(() => {
    setIsOver(false);
    onGuide(null);
  }, [onGuide]);

  const onDrop = useCallback(
    (event: DragEvent<HTMLElement>) => {
      setIsOver(false);
      onGuide(null);
      const data = readFootageDragData(event.dataTransfer);
      const place = placeOf(event);
      if (readOnly || data === null || place === null) {
        return;
      }
      event.preventDefault();
      const id = newDocId();
      change((doc) =>
        insertClip(
          doc,
          { id, source: data.source, atSec: place.sec, lengthSec: dropLengthSec(songMap.bpm, data.durationSec) },
          () => newDocId(),
          limitsFor(data)
        )
      );
      select({ kind: 'clip', id });
    },
    [change, limitsFor, onGuide, placeOf, readOnly, select, songMap.bpm]
  );

  return { isOver, onDragOver, onDragLeave, onDrop };
}
